/**
 * Product and Variant Helper Utilities
 *
 * Shared utilities for working with products, variants, and selections
 * to maintain DRY principles across Samples, Rebates, and Deals features.
 */

import type { Product } from '../models/product'
import type { ProductWithVariants } from '../components/UI/ProductVariantSelector'

/**
 * Maps a variant from various source formats to the standardized ProductWithVariants variant format.
 *
 * Used in:
 * - ProductVariantSelector: Converting from ProductVariantSelection
 * - ProductSelectionStep: Building selections from line items
 * - CreateRebateOfferModal: Converting from form data
 *
 * @example
 * const displayVariant = mapVariantToDisplay({
 *   id: 123,
 *   name: "French Fries",
 *   format_pack_size: "6/5lb",
 *   sku: "FF-001"
 * })
 */
export function mapVariantToDisplay(variant: {
  id?: number
  name?: string
  format_pack_size?: string | null
  sku?: string
}): ProductWithVariants['variants'][0] {
  return {
    id: variant.id || 0,
    name: variant.name || '',
    format_pack_size: variant.format_pack_size ?? null,
    sku: variant.sku || '',
  }
}

/**
 * Builds a selectedVariants record for Rebates form data.
 *
 * Rebates store variant selections as: { "productId_variantId": true }
 *
 * Used in:
 * - CreateRebateOfferModal.handleAddProduct
 * - CreateRebateOfferModal.handleEditProduct
 *
 * @example
 * const record = buildSelectedVariantsRecord(427, [53, 54])
 * // Returns: { "427_53": true, "427_54": true }
 */
export function buildSelectedVariantsRecord(
  productId: number,
  variantIds: number[]
): Record<string, boolean> {
  const record: Record<string, boolean> = {}
  variantIds.forEach((variantId) => {
    record[`${productId}_${variantId}`] = true
  })
  return record
}

/**
 * Finds a product by ID with flexible type handling.
 *
 * Handles both string and number IDs, commonly needed when
 * working with form values (strings) vs model data (numbers).
 *
 * Used in:
 * - ProductVariantSelector: Finding products for modal
 * - ProductSelectionStep: Finding products during conversions
 * - CreateRebateOfferModal: Finding products for selections
 *
 * @example
 * const product = findProductById(products, "427")
 * const product = findProductById(products, 427)
 */
export function findProductById(
  products: Product[] | undefined,
  productId: number | string | null | undefined
): Product | undefined {
  if (!productId || !products) return undefined
  const id = typeof productId === 'string' ? Number(productId) : productId
  return products.find((p) => p.id === id)
}

/**
 * Checks if a product has no variants.
 *
 * Used in:
 * - ProductVariantSelector: Validation before opening modal
 * - ProductVariantSelector: Showing "no variants" warning
 *
 * @example
 * if (hasProductNoVariants(products, productId)) {
 *   // Show warning or disable selection
 * }
 */
export function hasProductNoVariants(
  products: Product[] | undefined,
  productId: string | number | null | undefined
): boolean {
  if (!productId) return false
  const product = findProductById(products, productId)
  return !!product && (!product.variants || product.variants.length === 0)
}

/**
 * Finds all line item indices that belong to a specific product.
 *
 * Used in Samples feature where line items are stored as a flat array
 * and need to be grouped by product.
 *
 * Used in:
 * - ProductSelectionStep.handleEditVariant: Finding items to remove/update
 * - ProductSelectionStep.handleRemoveVariant: Finding items to remove
 * - ProductSelectionStep.renderAdditionalContent: Finding the line item for a variant
 *
 * @example
 * const indices = findLineItemIndicesForProduct(
 *   fields,
 *   427,
 *   watch,
 *   products
 * )
 * // Returns: [0, 1, 2] - indices of line items for product 427
 */
export function findLineItemIndicesForProduct(
  fields: any[],
  productId: number,
  watch: (name: string) => any,
  products: Product[] | undefined
): number[] {
  const indices: number[] = []

  fields.forEach((field: any, idx) => {
    const variantId = watch(`line_items.${idx}.variant_id`)
    if (variantId && products) {
      for (const product of products) {
        const variant = product.variants?.find((v) => v.id === variantId)
        if (variant && product.id === productId) {
          indices.push(idx)
          break
        }
      }
    }
  })

  return indices
}

/**
 * Finds the line item index for a specific variant.
 *
 * Used in Samples when rendering additional content for variants
 * and needing to access the form field for that variant.
 *
 * @example
 * const index = findLineItemIndexForVariant(fields, 53, watch)
 * if (index !== -1) {
 *   // Access form field at `line_items.${index}.quantity`
 * }
 */
export function findLineItemIndexForVariant(
  fields: any[],
  variantId: number,
  watch: (name: string) => any
): number {
  return fields.findIndex((field: any, idx) => {
    return watch(`line_items.${idx}.variant_id`) === variantId
  })
}
